'use client';

import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { certsDueAskRequest, type CertsDueBlock, type CertsDueRow, type CertsDueState } from '@/lib/chat/blocks/certs-due';

/**
 * Certificates due in a range: a count per week, a chip per type, the first
 * few customers, and the way through to the page that holds them.
 *
 * 🚨 Nothing here computes a figure the server did not. Tiles and chips read
 * the counts off the block; switching a chip only changes which of the
 * server's views is drawn.
 *
 * 🚨 Nothing here sends. "Ask Mucka to send reminders" puts the owner's own
 * words into the chat; Mucka then goes through the reminder cards that already
 * exist, one customer at a time, each with its own read-back. That is why the
 * button is a quiet plate and not orange: the tap asks, it does not send.
 */
const STATE_LABEL: Record<Exclude<CertsDueState, null>, string> = {
  booked: 'Booked',
  reminded: 'Reminded',
  card_waiting: 'Card waiting',
};

function Row({ row, onOpen }: { row: CertsDueRow; onOpen: (path: string) => void }) {
  const who = row.postcode ? `${row.who} · ${row.postcode}` : row.who;
  return (
    <button type="button" role="listitem" className="mchat-row mchat-certs-row" onClick={() => onOpen(row.path)}>
      <span className="mchat-row-main">
        <span className="mchat-row-title">{who}</span>
        <span className="mchat-certs-meta">
          <span>{row.type}</span>
          {row.tenant && <span className="mchat-certs-pill mchat-certs-pill--tenant">Tenant</span>}
          {row.state && <span className="mchat-certs-pill">{STATE_LABEL[row.state]}</span>}
        </span>
      </span>
      <span className={`mchat-certs-due${row.overdue ? ' mchat-certs-due--late' : ''}`}>
        {row.overdue ? `Overdue · ${row.dueLabel}` : row.dueLabel}
      </span>
      <Icon icon={ChevronRight} size={15} className="mchat-row-go" />
    </button>
  );
}

export function CertsDue({ block, live, onAnswer, onOpen }: {
  block: CertsDueBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}) {
  const [viewKey, setViewKey] = useState('all');
  const [asked, setAsked] = useState(false);
  const view = block.types.find(t => t.key === viewKey) ?? block.all;
  const busiest = Math.max(0, ...view.weeks);
  const notShown = view.total - view.rows.length;

  return (
    <div className="mchat-certs">
      {block.tiles.length > 0 && (
        <div className="mchat-certs-weeks" role="list" aria-label={`${view.total} due ${block.rangeLabel}, by week`}>
          {block.tiles.map((tile, i) => {
            const n = view.weeks[i] ?? 0;
            const tone = n === 0 ? ' mchat-certs-week--none' : n === busiest ? ' mchat-certs-week--top' : '';
            return (
              <div key={tile.from} role="listitem" className={`mchat-certs-week${tone}`} aria-label={`${tile.label}: ${n}`}>
                <span className="mchat-certs-week-label">{tile.label}</span>
                <span className="mchat-certs-week-count">{n}</span>
              </div>
            );
          })}
        </div>
      )}

      {block.types.length > 0 && (
        <div className="mchat-certs-chips" role="tablist" aria-label="Type">
          {[block.all, ...block.types].map(v => (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={v.key === view.key}
              className={`mchat-certs-chip${v.key === view.key ? ' mchat-certs-chip--on' : ''}`}
              onClick={() => setViewKey(v.key)}
            >
              {v.label} <span className="mchat-certs-chip-n">{v.total}</span>
            </button>
          ))}
        </div>
      )}

      <div role="list" aria-label={`${view.total} due ${block.rangeLabel}`}>
        {view.rows.map(row => <Row key={row.id} row={row} onOpen={onOpen} />)}
      </div>
      {notShown > 0 && <p className="mchat-certs-more">{notShown} more not shown</p>}

      {block.links.map(link => (
        <button key={link.path} type="button" className="mchat-row" onClick={() => onOpen(link.path)}>
          <span className="mchat-row-main"><span className="mchat-row-title">{link.label}</span></span>
          <Icon icon={ChevronRight} size={15} className="mchat-row-go" />
        </button>
      ))}

      {block.notes.length > 0 && (
        <div className="mchat-certs-notes">
          {block.notes.map(n => <p key={n}>{n}</p>)}
        </div>
      )}

      <div className="mchat-certs-foot">
        {block.canAsk ? (
          <>
            <button
              type="button"
              className="mchat-certs-ask"
              disabled={!live || asked}
              onClick={() => { setAsked(true); onAnswer(certsDueAskRequest(block)); }}
            >
              {asked ? 'Asked' : 'Ask Mucka to send reminders'}
            </button>
            <p className="mchat-certs-small">
              {block.cardsWaiting === 1 ? '1 reminder card is waiting.' : `${block.cardsWaiting} reminder cards are waiting.`}
              {' '}Mucka shows you each one before anything is sent.
            </p>
          </>
        ) : (
          <p className="mchat-certs-small">
            {block.cardsWaiting > 0
              ? 'Reminder cards are waiting on your Actions list. Nothing is sent from here.'
              : 'Mucka puts a reminder card on your Actions list as each one gets close. Nothing is sent from here.'}
          </p>
        )}
      </div>
    </div>
  );
}
